import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Gamepad2, Clock, DollarSign, Upload, AlertTriangle, Trophy,
  X, CheckCircle, XCircle, User, ExternalLink, Loader2, RefreshCw,
  Image, ZoomIn, Handshake, SkipForward, Timer, AlertCircle, Share2, Copy, Link
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import MatchChat from '../components/MatchChat';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Match timer duration (2 hours in milliseconds)
const MATCH_DURATION = 2 * 60 * 60 * 1000;
// Warning threshold (5 minutes before end)
const WARNING_THRESHOLD = 5 * 60 * 1000;

// Media Preview Component (Images and Videos with auto-play)
const MediaPreview = ({ url, label, aiValid, aiNotes }) => {
  const [isValidImage, setIsValidImage] = useState(false);
  const [isVideo, setIsVideo] = useState(false);

  useEffect(() => {
    if (url) {
      // Check if it's a video
      const videoExtensions = ['.mp4', '.mov', '.webm', '.avi'];
      const isVideoFile = videoExtensions.some(ext => url.toLowerCase().includes(ext)) ||
                          url.includes('video/');
      
      if (isVideoFile) {
        setIsVideo(true);
        setIsValidImage(false);
      } else {
        const img = new window.Image();
        img.onload = () => {
          setIsValidImage(true);
          setIsVideo(false);
        };
        img.onerror = () => {
          setIsValidImage(false);
          setIsVideo(false);
        };
        img.src = url;
      }
    }
  }, [url]);

  if (!url) return null;

  return (
    <div className="mt-2">
      {/* AI Validation Badge */}
      {aiValid !== null && aiValid !== undefined && (
        <div className={`mb-2 px-3 py-1 rounded-full inline-flex items-center gap-2 text-xs ${
          aiValid ? 'bg-[#00FF94]/20 text-[#00FF94]' : 'bg-[#FFB800]/20 text-[#FFB800]'
        }`}>
          {aiValid ? (
            <>
              <CheckCircle className="w-3 h-3" />
              AI Verified
            </>
          ) : (
            <>
              <AlertTriangle className="w-3 h-3" />
              AI Flagged
            </>
          )}
          {aiNotes && <span className="text-white/50">- {aiNotes.slice(0, 50)}</span>}
        </div>
      )}

      {isVideo ? (
        <Dialog>
          <DialogTrigger asChild>
            <div className="relative cursor-pointer group">
              <video 
                src={url}
                className="w-full max-w-[200px] h-auto rounded-lg border border-white/10 object-cover"
                style={{ maxHeight: '150px' }}
                muted
                loop
                autoPlay
                playsInline
              />
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                <ZoomIn className="w-6 h-6 text-white" />
              </div>
              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
                Video
              </div>
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-4xl bg-[#0A0A0B] border-white/10">
            <video 
              src={url}
              className="w-full h-auto rounded-lg"
              controls
              autoPlay
              playsInline
            />
          </DialogContent>
        </Dialog>
      ) : isValidImage ? (
        <Dialog>
          <DialogTrigger asChild>
            <div className="relative cursor-pointer group">
              <img 
                src={url} 
                alt={label}
                className="w-full max-w-[200px] h-auto rounded-lg border border-white/10 object-cover"
                style={{ maxHeight: '150px' }}
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                <ZoomIn className="w-6 h-6 text-white" />
              </div>
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-4xl bg-[#0A0A0B] border-white/10">
            <img 
              src={url} 
              alt={label}
              className="w-full h-auto rounded-lg"
            />
          </DialogContent>
        </Dialog>
      ) : (
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-[#00F0FF] text-sm flex items-center gap-1 hover:underline"
        >
          <ExternalLink className="w-3 h-3" /> View proof
        </a>
      )}
    </div>
  );
};

// Legacy ImagePreview for backwards compatibility
const ImagePreview = ({ url, label }) => {
  return <MediaPreview url={url} label={label} aiValid={null} aiNotes={null} />;
};

// Countdown Timer Component
const CountdownTimer = ({ startedAt, onExpired, onWarning }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isWarning, setIsWarning] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const expiredRef = useRef(false);
  const warningRef = useRef(false);

  useEffect(() => {
    if (!startedAt) return;

    const calculateTimeLeft = () => {
      const start = new Date(startedAt).getTime();
      const end = start + MATCH_DURATION;
      const now = Date.now();
      const remaining = end - now;

      if (remaining <= 0) {
        setTimeLeft(0);
        setIsExpired(true);
        if (!expiredRef.current) {
          expiredRef.current = true;
          onExpired?.();
        }
        return 0;
      }

      if (remaining <= WARNING_THRESHOLD && !warningRef.current) {
        warningRef.current = true;
        setIsWarning(true);
        onWarning?.();
      }

      setTimeLeft(remaining);
      return remaining;
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [startedAt, onExpired, onWarning]);

  if (timeLeft === null) return null;

  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

  const formatTime = () => {
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
        isExpired 
          ? 'bg-[#FF2E2E]/20 border border-[#FF2E2E]/50' 
          : isWarning 
            ? 'bg-[#FFB800]/20 border border-[#FFB800]/50 animate-pulse' 
            : 'bg-white/5 border border-white/10'
      }`}
    >
      <Timer className={`w-5 h-5 ${isExpired ? 'text-[#FF2E2E]' : isWarning ? 'text-[#FFB800]' : 'text-[#00F0FF]'}`} />
      <span className={`font-mono text-lg font-bold ${isExpired ? 'text-[#FF2E2E]' : isWarning ? 'text-[#FFB800]' : 'text-white'}`}>
        {isExpired ? 'EXPIRED' : formatTime()}
      </span>
    </div>
  );
};

const MatchDetail = () => {
  const { matchId } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proofUrl, setProofUrl] = useState('');
  const [claimedWin, setClaimedWin] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [requestingRematch, setRequestingRematch] = useState(false);
  
  // New states for enhanced proof system
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [requestingDraw, setRequestingDraw] = useState(false);
  const [requestingBypass, setRequestingBypass] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [freezingMatch, setFreezingMatch] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMatch();
  }, [matchId]);

  const fetchMatch = async () => {
    try {
      const response = await axios.get(`${API}/matches/${matchId}`, { withCredentials: true });
      const matchData = response.data;
      setMatch(matchData);
      
      // If match is pending_acceptance (waiting for creator to confirm) and user is the creator, show confirm button
      if (matchData.status === 'pending_acceptance') {
        const isParticipant = matchData.creator_id === user?.user_id || matchData.opponent_id === user?.user_id;
        if (isParticipant) {
          navigate(`/match/${matchId}/confirm`);
          return;
        }
      }
      
      // Legacy support for 'reserved' status
      if (matchData.status === 'reserved') {
        const isParticipant = matchData.creator_id === user?.user_id || matchData.opponent_id === user?.user_id;
        if (isParticipant) {
          navigate(`/match/${matchId}/confirm`);
          return;
        }
      }
    } catch (error) {
      console.error('Error fetching match:', error);
      toast.error('Match not found');
      navigate('/lobby');
    } finally {
      setLoading(false);
    }
  };

  const handleTimerExpired = useCallback(() => {
    setTimerExpired(true);
    toast.error('Match timer has expired! Please submit results or contact admin.');
  }, []);

  const handleTimerWarning = useCallback(() => {
    setShowWarning(true);
    toast.warning('5 minutes remaining! Submit your results now.');
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an image (JPG, PNG, GIF, WebP) or video (MP4, MOV)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('match_id', matchId);

      const response = await axios.post(`${API}/matches/upload-proof`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setUploadedFile({
          name: file.name,
          url: response.data.proof_url
        });
        setProofUrl(response.data.proof_url);
        toast.success('File uploaded successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleRematch = async () => {
    setRequestingRematch(true);
    try {
      const response = await axios.post(
        `${API}/matches/rematch`,
        { original_match_id: matchId },
        { withCredentials: true }
      );
      toast.success(`Rematch request sent to ${response.data.opponent_name}!`);
      await refreshUser();
      navigate(`/match/${response.data.match_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request rematch');
    } finally {
      setRequestingRematch(false);
    }
  };

  const handleSubmitProof = async () => {
    if (!proofUrl.trim()) {
      toast.error('Please upload proof or enter a proof URL');
      return;
    }
    if (claimedWin === null) {
      toast.error('Please indicate if you won or lost');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/matches/submit-proof`,
        {
          match_id: matchId,
          proof_url: proofUrl,
          claimed_win: claimedWin
        },
        { withCredentials: true }
      );
      toast.success('Proof submitted successfully!');
      await fetchMatch();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit proof');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestDraw = async () => {
    setRequestingDraw(true);
    try {
      await axios.post(`${API}/matches/request-draw/${matchId}`, {}, { withCredentials: true });
      toast.success('Draw request sent to opponent!');
      await fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request draw');
    } finally {
      setRequestingDraw(false);
    }
  };

  const handleAcceptDraw = async () => {
    try {
      await axios.post(`${API}/matches/accept-draw/${matchId}`, {}, { withCredentials: true });
      toast.success('Draw accepted! Both players refunded.');
      await fetchMatch();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept draw');
    }
  };

  const handleDeclineDraw = async () => {
    try {
      await axios.post(`${API}/matches/decline-draw/${matchId}`, {}, { withCredentials: true });
      toast.info('Draw request declined');
      await fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline draw');
    }
  };

  const handleRequestBypass = async () => {
    setRequestingBypass(true);
    try {
      await axios.post(`${API}/matches/request-bypass/${matchId}`, {}, { withCredentials: true });
      toast.success('Bypass proof request sent to opponent!');
      await fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request bypass');
    } finally {
      setRequestingBypass(false);
    }
  };

  const handleAcceptBypass = async () => {
    try {
      await axios.post(`${API}/matches/accept-bypass/${matchId}`, {}, { withCredentials: true });
      toast.success('Bypass accepted! Proof no longer required.');
      await fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept bypass');
    }
  };

  const handleFreezeMatch = async () => {
    setFreezingMatch(true);
    try {
      await axios.post(`${API}/matches/freeze/${matchId}`, {}, { withCredentials: true });
      toast.info('Match frozen and sent to admin for resolution');
      await fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to freeze match');
    } finally {
      setFreezingMatch(false);
    }
  };

  const handleCancelMatch = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/matches/cancel/${matchId}`, {}, { withCredentials: true });
      toast.success('Match cancelled');
      await refreshUser();
      navigate('/lobby');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel match');
    } finally {
      setCancelling(false);
    }
  };

  const handleFileDispute = async () => {
    if (!disputeReason.trim()) {
      toast.error('Please provide a reason for the dispute');
      return;
    }

    try {
      await axios.post(
        `${API}/matches/dispute/${matchId}?reason=${encodeURIComponent(disputeReason)}`,
        {},
        { withCredentials: true }
      );
      toast.success('Dispute filed');
      await fetchMatch();
      setShowDisputeForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to file dispute');
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'open':
        return { color: '#00F0FF', bg: 'bg-[#00F0FF]/10', text: 'Waiting for Opponent' };
      case 'in_progress':
        return { color: '#FFB800', bg: 'bg-[#FFB800]/10', text: 'In Progress' };
      case 'awaiting_proof':
        return { color: '#7000FF', bg: 'bg-[#7000FF]/10', text: 'Awaiting Proof' };
      case 'disputed':
        return { color: '#FF2E2E', bg: 'bg-[#FF2E2E]/10', text: 'Disputed' };
      case 'frozen':
        return { color: '#FF6B35', bg: 'bg-[#FF6B35]/10', text: 'Frozen - Admin Review' };
      case 'completed':
        return { color: '#00FF94', bg: 'bg-[#00FF94]/10', text: 'Completed' };
      case 'cancelled':
        return { color: '#FFFFFF', bg: 'bg-white/10', text: 'Cancelled' };
      default:
        return { color: '#FFFFFF', bg: 'bg-white/10', text: status };
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!match) return null;

  const statusConfig = getStatusConfig(match.status);
  const isCreator = match.creator_id === user?.user_id;
  const isOpponent = match.opponent_id === user?.user_id;
  const isParticipant = isCreator || isOpponent;
  const myProof = isCreator ? match.creator_proof : match.opponent_proof;
  const myClaim = isCreator ? match.creator_claimed_win : match.opponent_claimed_win;
  const opponentProof = isCreator ? match.opponent_proof : match.creator_proof;
  const opponentClaim = isCreator ? match.opponent_claimed_win : match.creator_claimed_win;
  const isWinner = match.winner_id === user?.user_id;
  
  // Draw/Bypass states
  const drawRequestedByMe = match.draw_requested_by === user?.user_id;
  const drawRequestedByOpponent = match.draw_requested_by && match.draw_requested_by !== user?.user_id;
  const bypassRequestedByMe = match.bypass_requested_by === user?.user_id;
  const bypassRequestedByOpponent = match.bypass_requested_by && match.bypass_requested_by !== user?.user_id;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="match-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-3xl font-bold">{match.game_name}</h1>
              <span 
                className={`text-sm px-3 py-1 rounded-full ${statusConfig.bg}`}
                style={{ color: statusConfig.color }}
              >
                {statusConfig.text}
              </span>
            </div>
            <p className="text-white/50">Match ID: {match.match_id}</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Timer for in-progress matches */}
            {['in_progress', 'awaiting_proof'].includes(match.status) && match.started_at && (
              <CountdownTimer 
                startedAt={match.started_at}
                onExpired={handleTimerExpired}
                onWarning={handleTimerWarning}
              />
            )}
            
            {match.status === 'open' && isCreator && (
              <Button
                variant="outline"
                onClick={handleCancelMatch}
                disabled={cancelling}
                className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                data-testid="cancel-match-btn"
              >
                <X className="w-4 h-4 mr-2" />
                {cancelling ? 'Cancelling...' : 'Cancel Match'}
              </Button>
            )}
          </div>
        </div>

        {/* Timer Warning Banner */}
        {showWarning && !timerExpired && ['in_progress', 'awaiting_proof'].includes(match.status) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl bg-[#FFB800]/20 border border-[#FFB800]/50"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-[#FFB800]" />
              <div>
                <p className="font-semibold text-[#FFB800]">5 Minutes Until Funds Frozen!</p>
                <p className="text-white/70 text-sm">Please submit your game results now to avoid admin review.</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Timer Expired Banner */}
        {timerExpired && ['in_progress', 'awaiting_proof'].includes(match.status) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl bg-[#FF2E2E]/20 border border-[#FF2E2E]/50"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-[#FF2E2E]" />
                <div>
                  <p className="font-semibold text-[#FF2E2E]">Match Timer Expired!</p>
                  <p className="text-white/70 text-sm">Please submit correct game results or the match will be sent to admin.</p>
                </div>
              </div>
              <Button
                onClick={handleFreezeMatch}
                disabled={freezingMatch}
                className="bg-[#FF2E2E] text-white hover:bg-[#FF2E2E]/90"
                data-testid="freeze-match-btn"
              >
                {freezingMatch ? 'Sending...' : 'Send to Admin'}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Match Started Banner - Shows when match is in progress */}
        {['in_progress', 'awaiting_proof'].includes(match.status) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 p-4 rounded-xl bg-gradient-to-r from-[#00FF94]/20 to-[#00F0FF]/20 border border-[#00FF94]/30 text-center"
          >
            <div className="flex items-center justify-center gap-3">
              <div className="w-3 h-3 rounded-full bg-[#00FF94] animate-pulse" />
              <p className="font-heading text-lg font-bold text-[#00FF94]">Match Started!</p>
              <div className="w-3 h-3 rounded-full bg-[#00FF94] animate-pulse" />
            </div>
            <p className="text-white/60 text-sm mt-1">Play your game and submit proof when finished</p>
          </motion.div>
        )}

        {/* Match Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8 mb-8"
        >
          {/* Players */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Creator */}
            <div className={`p-4 rounded-xl ${isCreator ? 'bg-[#00F0FF]/10 border border-[#00F0FF]/30' : 'bg-white/5'}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#7000FF]" />
                </div>
                <div>
                  <p className="text-xs text-white/40">CHALLENGER</p>
                  <p className="font-semibold">{match.creator_name}</p>
                </div>
              </div>
              {isCreator && <span className="text-xs text-[#00F0FF]">You</span>}
            </div>

            {/* VS */}
            <div className="flex items-center justify-center">
              <div className="text-center">
                <span className="font-heading text-4xl font-bold text-white/20">VS</span>
                <p 
                  className="font-mono text-2xl font-bold text-[#CCFF00] mt-2"
                  style={{ textShadow: '0 0 20px rgba(204, 255, 0, 0.5)' }}
                >
                  {(match.wager_amount * 100).toLocaleString()} credits
                </p>
                <p className="text-xs text-white/40 mt-1">each</p>
              </div>
            </div>

            {/* Opponent */}
            <div className={`p-4 rounded-xl ${isOpponent ? 'bg-[#00F0FF]/10 border border-[#00F0FF]/30' : 'bg-white/5'}`}>
              {match.opponent_id ? (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-[#FF2E2E]/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#FF2E2E]" />
                    </div>
                    <div>
                      <p className="text-xs text-white/40">OPPONENT</p>
                      <p className="font-semibold">{match.opponent_name}</p>
                    </div>
                  </div>
                  {isOpponent && <span className="text-xs text-[#00F0FF]">You</span>}
                </>
              ) : (
                <div className="text-center py-4">
                  <Clock className="w-8 h-8 text-white/20 mx-auto mb-2" />
                  <p className="text-white/40 text-sm mb-4">Waiting for opponent...</p>
                  
                  {/* Invite Opponent Buttons */}
                  {isCreator && match.status === 'open' && (
                    <div className="space-y-2">
                      <Button
                        onClick={() => {
                          const matchUrl = `https://matchstake.online/join/${match.match_id}`;
                          const shareText = `I just challenged you to a MatchStake game for $${match.wager_amount?.toFixed(0) || match.wager_amount}.\n\nAccept here:\n${matchUrl}`;
                          
                          if (navigator.share) {
                            navigator.share({
                              title: 'MatchStake Challenge',
                              text: shareText,
                              url: matchUrl
                            }).catch(err => {
                              // User cancelled or share failed
                              console.log('Share cancelled or failed:', err);
                            });
                          } else {
                            // Fallback: copy to clipboard
                            navigator.clipboard.writeText(shareText).then(() => {
                              toast.success('Challenge message copied to clipboard!');
                            }).catch(() => {
                              toast.error('Failed to copy');
                            });
                          }
                        }}
                        className="w-full bg-[#00FF94] text-black hover:bg-[#00FF94]/90"
                        data-testid="invite-opponent-btn"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Invite Opponent
                      </Button>
                      <Button
                        onClick={() => {
                          const matchUrl = `https://matchstake.online/join/${match.match_id}`;
                          navigator.clipboard.writeText(matchUrl).then(() => {
                            toast.success('Match link copied!');
                          }).catch(() => {
                            toast.error('Failed to copy link');
                          });
                        }}
                        variant="outline"
                        className="w-full btn-secondary"
                        data-testid="copy-match-link-btn"
                      >
                        <Link className="w-4 h-4 mr-2" />
                        Copy Match Link
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Competition Reward Info */}
          <div className="p-4 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20 text-center mb-6">
            <p className="text-white/50 text-sm mb-1">Winner Receives</p>
            <p 
              className="font-mono text-3xl font-bold text-[#00FF94]"
              style={{ textShadow: '0 0 30px rgba(0, 255, 148, 0.5)' }}
            >
              Reward: ${match.prize_amount?.toFixed(2) || ((match.wager_amount * 2) - (match.platform_fee * 2)).toFixed(2)}
            </p>
          </div>

          {/* Completed Match Result */}
          {match.status === 'completed' && (
            <div className={`p-6 rounded-xl text-center ${isWinner ? 'bg-[#00FF94]/10 border border-[#00FF94]/30' : 'bg-white/5'}`}>
              <Trophy className={`w-12 h-12 mx-auto mb-3 ${isWinner ? 'text-[#00FF94]' : 'text-white/30'}`} />
              <p className="font-heading text-xl font-bold mb-1">
                {match.draw_accepted ? 'Draw!' : isWinner ? 'Victory!' : 'Defeat'}
              </p>
              <p className="text-white/50">
                {match.draw_accepted 
                  ? 'Match ended in mutual draw. Credits returned.'
                  : isWinner 
                    ? `You won a $${match.prize_amount?.toFixed(2) || ((match.wager_amount * 2) - (match.platform_fee * 2)).toFixed(2)} reward!`
                    : `Better luck next time`
                }
              </p>
            </div>
          )}

          {/* Disputed */}
          {match.status === 'disputed' && (
            <div className="p-6 rounded-xl bg-[#FF2E2E]/10 border border-[#FF2E2E]/30 text-center">
              <AlertTriangle className="w-12 h-12 text-[#FF2E2E] mx-auto mb-3" />
              <p className="font-heading text-xl font-bold mb-1">Match Disputed</p>
              <p className="text-white/50 text-sm mb-2">{match.dispute_reason}</p>
              <p className="text-white/40 text-xs">An admin will review this match and make a decision.</p>
            </div>
          )}

          {/* Frozen */}
          {match.status === 'frozen' && (
            <div className="p-6 rounded-xl bg-[#FF6B35]/10 border border-[#FF6B35]/30 text-center">
              <AlertTriangle className="w-12 h-12 text-[#FF6B35] mx-auto mb-3" />
              <p className="font-heading text-xl font-bold mb-1">Match Frozen</p>
              <p className="text-white/50 text-sm mb-2">This match has been sent to admin for resolution due to timeout.</p>
              <p className="text-white/40 text-xs">You will be notified when the admin makes a decision.</p>
            </div>
          )}
        </motion.div>

        {/* Draw/Bypass Request Notifications */}
        {isParticipant && ['in_progress', 'awaiting_proof'].includes(match.status) && (
          <>
            {/* Incoming Draw Request */}
            {drawRequestedByOpponent && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-2xl p-6 mb-6 border border-[#00F0FF]/30"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Handshake className="w-6 h-6 text-[#00F0FF]" />
                  <div>
                    <p className="font-semibold">Draw Request Received</p>
                    <p className="text-white/50 text-sm">Your opponent wants to end this match as a draw.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleAcceptDraw}
                    className="flex-1 btn-primary"
                    data-testid="accept-draw-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Accept Draw
                  </Button>
                  <Button
                    onClick={handleDeclineDraw}
                    variant="outline"
                    className="flex-1 btn-secondary"
                    data-testid="decline-draw-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Decline
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Incoming Bypass Request */}
            {bypassRequestedByOpponent && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-2xl p-6 mb-6 border border-[#7000FF]/30"
              >
                <div className="flex items-center gap-3 mb-4">
                  <SkipForward className="w-6 h-6 text-[#7000FF]" />
                  <div>
                    <p className="font-semibold">Bypass Proof Request</p>
                    <p className="text-white/50 text-sm">Your opponent wants to skip proof submission.</p>
                  </div>
                </div>
                <Button
                  onClick={handleAcceptBypass}
                  className="w-full bg-[#7000FF] text-white hover:bg-[#7000FF]/90"
                  data-testid="accept-bypass-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accept - Skip Proof Requirement
                </Button>
              </motion.div>
            )}
          </>
        )}

        {/* Match Chat Section - Available during active matches */}
        {isParticipant && ['pending_acceptance', 'active', 'in_progress', 'awaiting_proof', 'disputed'].includes(match.status) && (
          <div className="mb-8">
            <MatchChat 
              matchId={matchId} 
              matchStatus={match.status}
              participants={{
                creator: { user_id: match.creator_id, name: match.creator_name },
                opponent: { user_id: match.opponent_id, name: match.opponent_name }
              }}
            />
          </div>
        )}

        {/* Proof Section */}
        {isParticipant && ['in_progress', 'awaiting_proof'].includes(match.status) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-8 mb-8"
          >
            <h2 className="font-heading font-semibold text-xl mb-6">Submit Proof</h2>

            {myProof ? (
              <div className="mb-6">
                <p className="text-[#00FF94] flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  Your proof submitted
                </p>
                <MediaPreview 
                  url={myProof} 
                  label="Your proof"
                  aiValid={isCreator ? match.creator_proof_ai_valid : match.opponent_proof_ai_valid}
                  aiNotes={isCreator ? match.creator_proof_ai_notes : match.opponent_proof_ai_notes}
                />
                <p className="text-white/50 text-sm mt-2">
                  You claimed: <span className={myClaim ? 'text-[#00FF94]' : 'text-[#FF2E2E]'}>{myClaim ? 'Win' : 'Loss'}</span>
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* File Upload */}
                <div>
                  <label className="text-sm text-white/50 mb-2 block">Upload Screenshot/Video</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/mp4,video/quicktime"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="proof-file-input"
                  />
                  
                  {uploadedFile ? (
                    <div className="p-4 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-[#00FF94]" />
                          <div>
                            <p className="text-[#00FF94] font-medium">File uploaded</p>
                            <p className="text-white/50 text-sm">{uploadedFile.name}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setUploadedFile(null);
                            setProofUrl('');
                          }}
                          className="text-white/50 hover:text-white"
                        >
                          Change
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full btn-secondary h-24 border-dashed"
                      data-testid="upload-proof-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 mr-2" />
                          Click to upload proof (Image/Video)
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* OR Divider */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-white/30 text-sm">OR</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* URL Input */}
                <div>
                  <label className="text-sm text-white/50 mb-2 block">Paste URL (Imgur, etc.)</label>
                  <Input
                    placeholder="https://imgur.com/..."
                    value={uploadedFile ? '' : proofUrl}
                    onChange={(e) => setProofUrl(e.target.value)}
                    disabled={!!uploadedFile}
                    className="input-dark"
                    data-testid="proof-url-input"
                  />
                </div>

                <div>
                  <label className="text-sm text-white/50 mb-2 block">Match Result</label>
                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setClaimedWin(true)}
                      className={`flex-1 ${claimedWin === true ? 'bg-[#00FF94]/20 border-[#00FF94] text-[#00FF94]' : 'btn-secondary'}`}
                      data-testid="claim-win-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      I Won
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setClaimedWin(false)}
                      className={`flex-1 ${claimedWin === false ? 'bg-[#FF2E2E]/20 border-[#FF2E2E] text-[#FF2E2E]' : 'btn-secondary'}`}
                      data-testid="claim-loss-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      I Lost
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={handleSubmitProof}
                  disabled={submitting || (!proofUrl.trim() && !match.bypass_accepted)}
                  className="w-full btn-primary clip-slant"
                  data-testid="submit-proof-btn"
                >
                  {submitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Submit Proof
                    </>
                  )}
                </Button>

                {/* Bypass accepted notice */}
                {match.bypass_accepted && (
                  <p className="text-[#7000FF] text-sm text-center">
                    Proof requirement has been bypassed by mutual agreement
                  </p>
                )}
              </div>
            )}

            {/* Opponent's Proof Status */}
            {match.opponent_id && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/50 text-sm mb-2">Opponent's Proof</p>
                {opponentProof ? (
                  <div>
                    <MediaPreview 
                      url={opponentProof} 
                      label="Opponent's proof"
                      aiValid={isCreator ? match.opponent_proof_ai_valid : match.creator_proof_ai_valid}
                      aiNotes={isCreator ? match.opponent_proof_ai_notes : match.creator_proof_ai_notes}
                    />
                    <p className="text-white/50 text-sm mt-2">
                      They claimed: <span className={opponentClaim ? 'text-[#00FF94]' : 'text-[#FF2E2E]'}>{opponentClaim ? 'Win' : 'Loss'}</span>
                    </p>
                  </div>
                ) : (
                  <p className="text-white/30 text-sm">Waiting for opponent to submit proof...</p>
                )}
              </div>
            )}

            {/* Draw/Bypass Options */}
            {!myProof && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/50 text-sm mb-4">Other Options</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Draw Button */}
                  {!match.draw_requested_by ? (
                    <Button
                      variant="outline"
                      onClick={handleRequestDraw}
                      disabled={requestingDraw}
                      className="flex-1 text-[#00F0FF] border-[#00F0FF]/30 hover:bg-[#00F0FF]/10"
                      data-testid="request-draw-btn"
                    >
                      <Handshake className="w-4 h-4 mr-2" />
                      {requestingDraw ? 'Requesting...' : 'Request Draw'}
                    </Button>
                  ) : drawRequestedByMe ? (
                    <div className="flex-1 p-3 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-center">
                      <p className="text-[#00F0FF] text-sm">Draw request sent - waiting for opponent</p>
                    </div>
                  ) : null}

                  {/* Bypass Button */}
                  {!match.bypass_requested_by && !match.bypass_accepted ? (
                    <Button
                      variant="outline"
                      onClick={handleRequestBypass}
                      disabled={requestingBypass}
                      className="flex-1 text-[#7000FF] border-[#7000FF]/30 hover:bg-[#7000FF]/10"
                      data-testid="request-bypass-btn"
                    >
                      <SkipForward className="w-4 h-4 mr-2" />
                      {requestingBypass ? 'Requesting...' : 'Bypass Proof'}
                    </Button>
                  ) : bypassRequestedByMe ? (
                    <div className="flex-1 p-3 rounded-lg bg-[#7000FF]/10 border border-[#7000FF]/30 text-center">
                      <p className="text-[#7000FF] text-sm">Bypass request sent - waiting for opponent</p>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* Dispute Option */}
            {myProof && !showDisputeForm && match.status !== 'disputed' && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <Button
                  variant="outline"
                  onClick={() => setShowDisputeForm(true)}
                  className="text-[#FFB800] border-[#FFB800]/30 hover:bg-[#FFB800]/10"
                  data-testid="open-dispute-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Report Issue / Dispute
                </Button>
              </div>
            )}

            {showDisputeForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-4 p-4 rounded-lg bg-[#FFB800]/10 border border-[#FFB800]/30"
              >
                <label className="text-sm text-white/70 mb-2 block">Reason for Dispute</label>
                <Textarea
                  placeholder="Explain why you're disputing this match..."
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value)}
                  className="input-dark mb-4"
                  rows={3}
                  data-testid="dispute-reason-input"
                />
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowDisputeForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleFileDispute}
                    className="bg-[#FFB800] text-black hover:bg-[#FFB800]/90"
                    data-testid="submit-dispute-btn"
                  >
                    File Dispute
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MatchDetail;
